import * as ko from 'knockout';

import { ListRequestParams, getContentRange } from '../api/request';
import { BaseLoadingScreen } from './base_loading_screen';
import {
  ListLoaderDelegate,
  ListLoader,
  ItemSelectionTracker,
  delegateActions,
  confirmBulkDelete,
} from '../components/list_loader';
import { formatAttributes } from '../models/attribute_meta';
import { DimensionMeta, AttributeFilters } from '../models/dimension_meta';
import { CropVariety } from '../models/crop_variety';
import { CROP_VARIETY_SLUG } from '../api/dimension_metas';
import * as dimensionsApi from '../api/dimensions';
import * as dimensionMetasApi from '../api/dimension_metas';
import * as cropVarietiesApi from '../api/crop_varieties';
import * as cropsApi from '../api/crops';
import * as namesApi from '../api/names';
import { breedersApi } from '../api/organizations';
import * as usersApi from '../api/users';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import { downloadBlob, updateLocationWithQueryString, asArray } from '../utils';
import { serializeDateTime, deflateList } from '../api/serialization';
import { ListHeaderAction } from '../components/list_header';
import { FilterDelegate } from '../components/list_filters';
import { session } from '../session';
import { Action } from '../components/basic_widgets';
import { DimensionRecordActions } from './dimension_records';
import {
  canCreateCropVariety,
  canDeleteCropVariety,
  canEditCropVariety,
  reactListTablesEnabled,
} from '../permissions';
import { removeDialog } from '../components/remove_dialog';
import { ShowArchivedFilter } from '../components/show_archived_filter';
import { GridView, refreshRows, pageParams } from '../utils/grid_view';
import { GridChoice, GridChoices, toChoices, listLookup } from '../utils/grid_choices';
import { ARCHIVED_HIDDEN_FILTER } from '@/lib/gridViewState';
import {
  CropVarietiesChoices,
  CropVarietiesColumn,
  CropVarietiesPage,
  CropVarietiesProps,
  CropVarietiesQuery,
  CropVarietiesRow,
  IslandGridSelection,
} from 'react-islands';

let template = require('raw-loader!../../templates/crop_varieties.html').default;

const showBreedingInfoKey = 'show-breeding-info-v0';

interface DefaultFilters {
  name_prefix?: string;
  anonymized_code?: string;
  crop_ids?: string | string[];
}

type CropVarietiesFilters = DefaultFilters & AttributeFilters;

class CropVarietiesScreen
  extends BaseLoadingScreen
  implements ListLoaderDelegate<cropVarietiesApi.CropVarietyData, CropVariety>
{
  canEdit = canEditCropVariety(session.tenant());
  canCreate = canCreateCropVariety();
  canDelete = canDeleteCropVariety();
  canViewDetails = session.isFieldtrials();
  loader: ListLoader<cropVarietiesApi.CropVarietyData, CropVariety>;
  showArchivedFilter: ShowArchivedFilter;
  dimensionRecordActions: DimensionRecordActions;

  private nameFilter = ko.observable('');
  private anonymizedCodeFilter = ko.observable('').extend({ throttle: 300 });
  private cropFilter = ko.observableArray<dimensionsApi.DimensionData>();
  private showBreedingInfoFilter = ko.observable(true);
  newFilters: FilterDelegate[] = [
    { title: i18n.t('Anonymized code')(), textValue: this.anonymizedCodeFilter },
    { title: i18n.t('Crop')(), entities: this.cropFilter, list: cropsApi.list },
  ];
  searchPlaceholder = i18n.t('Search varieties')();
  showBreedingInfo = ko.pureComputed(() => this.showBreedingInfoFilter());

  sortBys = ko.observableArray([ko.observable<string>('anonymized_code')]);
  sortingOptions = [
    { name: i18n.t('Anonymized code')(), value: 'anonymized_code' },
    { name: i18n.t('Name')(), value: 'name' },
  ];
  allowItemsPerLoadDisplay = true;
  allowEditFilters = true;

  dimensionMeta = ko.observable<DimensionMeta>(null);

  private canExport = ko.observable(false);
  exporting = ko.observable(false);

  listActions = ko.pureComputed<ListHeaderAction[]>(() => {
    let actions: ListHeaderAction[] = [];

    if (this.canCreate) {
      actions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/crop_varieties/new/',
        tooltipTitle: i18n.t('Add crop variety')(),
      });
    }
    if (this.canEdit) {
      actions.push(
        {
          title: i18n.t('Customize')(),
          icon: 'settings',
          href: '/dimensions/crop_variety/',
        },
        {
          title: i18n.t('Import stages')(),
          icon: 'approval',
          href: '/dimensions/crop_variety/import_stages/',
        },
        {
          title: i18n.t('Import')(),
          icon: 'file_upload',
          href: '/dimensions/crop_variety/import/',
          tooltipTitle: i18n.t('Import crop varieties')(),
        }
      );
    }

    if (this.canExport()) {
      actions.push({
        title: i18n.t('Export')(),
        icon: 'file_download',
        onClick: this.downloadExport,
        loading: this.exporting,
      });
    }

    return actions;
  });

  selectionTracker: ItemSelectionTracker<CropVariety>;
  selectedIds = ko.pureComputed(() => this.selectionTracker.selectedItems().map((cv) => cv.id()));
  bulkRemoveTooltipTitle = i18n.t('Delete selected varieties')();

  newVarietiesTable = reactListTablesEnabled();

  private tableColumns = ko.observableArray<CropVarietiesColumn>([]);
  /** A tenant has a handful, so they are listed once and the picker never searches. */
  private varietyTypes: GridChoice[] = [];

  // `varietyType` has no lookup: its whole vocabulary is seeded below, so nothing is ever resolved
  // or searched for it.
  private view = new GridView(
    'crop-varieties',
    new GridChoices<CropVarietiesChoices>(
      {
        crop: listLookup(cropsApi.list, 'crop'),
        // Two fields, two pickers: a variety bred with someone else's male parent has them filled the
        // other way round, so a shared lookup would file a second breeder under the first column.
        origin: listLookup(breedersApi.list, 'origin'),
        origin_2: listLookup(breedersApi.list, 'origin2'),
      },
      () => ({
        crop: [],
        varietyType: this.varietyTypes,
        origin: [],
        origin2: [],
      })
    )
  );

  tableProps = ko.pureComputed<CropVarietiesProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    columns: this.tableColumns(),
    canDelete: this.canDelete,
    onBulkDelete: this.onBulkDelete,
  }));

  private subscriptions: KnockoutSubscription[] = [];

  constructor(params: { filters: CropVarietiesFilters }) {
    super();

    const showBreedingInfoValue = localStorage.getItem(showBreedingInfoKey);
    this.showBreedingInfoFilter(showBreedingInfoValue === 'true' ? true : false);

    this.nameFilter(params.filters.name_prefix || '');
    // set afterwards, or we'd have to wait for throttle before updating initial value
    this.nameFilter = this.nameFilter.extend({ throttle: 300 });
    this.anonymizedCodeFilter(params.filters.anonymized_code || '');

    this.showArchivedFilter = new ShowArchivedFilter(dimensionMetasApi.CROP_VARIETY_SLUG, () => {
      if (this.loader) this.loader.forceLoad();
    });

    this.dimensionRecordActions = new DimensionRecordActions(
      () => this.canEdit,
      {
        onArchive: () => this.applyChange(),
        onUnarchive: () => this.applyChange(),
      },
      (record) => this.getUnarchiveReplacementWarnings(record.id)
    );

    let cropIds = asArray(params.filters.crop_ids);
    let dmPromise = dimensionMetasApi.retrieve(CROP_VARIETY_SLUG);
    let cropPromise =
      cropIds.length > 0 ? cropsApi.list({ ids: cropIds }) : Promise.resolve<cropsApi.CropData[]>([]);
    let mePromise = usersApi.me();
    let promise = Promise.all([dmPromise, cropPromise, mePromise]).then(([dmData, cropData, me]) => {
      this.dimensionMeta(new DimensionMeta(dmData));
      if (this.newVarietiesTable) {
        this.tableColumns(this.dimensionMeta().attributeColumns());
      } else {
        this.newFilters.push(...this.dimensionMeta().getAttributeFilters(params.filters));
      }

      if (cropData) {
        this.cropFilter(cropData);
      }

      this.canExport(me.role === 'admin');
    });
    this.loadedAfter(
      this.newVarietiesTable ? promise.then(() => this.loadTableView(params.filters)) : promise
    );

    this.subscriptions.push(this.showBreedingInfoFilter.subscribe(this.onShowBreedingInfoChanged));
  }

  onReady(loader: ListLoader<cropVarietiesApi.CropVarietyData, CropVariety>) {
    this.loader = loader;
    this.selectionTracker = new ItemSelectionTracker(this.loader.items);
    this.subscriptions.push(this.nameFilter.subscribe(() => this.loader.forceLoad()));
    this.subscriptions.push(this.showBreedingInfoFilter.subscribe(() => this.loader.forceLoad()));
  }

  onRefresh(cropVarieties: CropVariety[]): void {
    const selectedIds = new Set(this.selectedIds());
    const updatedSelected = cropVarieties.filter((cv) => selectedIds.has(cv.id()));
    this.selectionTracker.selectedItems(updatedSelected);
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }

  private getUnarchiveReplacementWarnings(cropVarietyId: string): Promise<string[]> {
    return cropVarietiesApi.retrieve(cropVarietyId).then((variety) => {
      const canonical = variety.replaced_by;
      if (!canonical) {
        return [];
      }
      const canonicalLabel = canonical.code ? canonical.name + ' (' + canonical.code + ')' : canonical.name;
      return [
        i18n.t(
          'This variety is currently replaced by "{{ name }}". Unarchiving it will undo that replacement.',
          { name: canonicalLabel }
        )(),
      ];
    });
  }

  private onShowBreedingInfoChanged = () => {
    localStorage.setItem(showBreedingInfoKey, this.showBreedingInfoFilter().toString());
  };

  fetch(params: ListRequestParams) {
    let filter = this.getFilters();
    updateLocationWithQueryString(filter);

    return cropVarietiesApi.list(
      {
        sort_by: this.sortBys()[0](),
        ...filter,
        ...params,
      },
      null,
      {
        injectTenant: true,
        onHeadersReceived: (headers) => {
          this.selectionTracker.total(getContentRange(headers).total);
        },
      }
    );
  }

  instantiate(cropVrietyData: cropVarietiesApi.CropVarietyData) {
    return new CropVariety(cropVrietyData, this.dimensionMeta());
  }

  remove(id: string) {
    return cropVarietiesApi.remove(id);
  }

  canRemove(cropVariety: CropVariety) {
    return this.canDelete;
  }

  confirmBulkRemove = () => {
    removeDialog(
      i18n.t(['crop_varieties_title', 'Crop Varieties'])().toLowerCase(),
      [],
      () =>
        cropVarietiesApi.bulkRemove({
          ...this.getFilters(),
          ...(this.selectionTracker.allSelected() ? {} : { ids: this.selectedIds() }),
          delete_limit: this.selectionTracker.selectedCount(),
        }),
      this.selectionTracker.selectedCount()
    )
      .then(() => {
        this.loader.refresh();
        this.selectionTracker.clear();
      })
      .catch(() => {});
  };

  downloadExport = () => {
    this.exporting(true);

    cropVarietiesApi
      .downloadExport(this.newVarietiesTable ? this.view.queryParams('name_prefix') : this.getFilters())
      .then((data) => {
        this.exporting(false);
        downloadBlob(data, 'crop-varieties-' + serializeDateTime(new Date()) + '.xlsx');
      })
      .catch(() => {
        this.exporting(false);
      });
  };

  private getFilters() {
    return {
      name_prefix: this.nameFilter(),
      anonymized_code: this.anonymizedCodeFilter(),
      crop_ids: deflateList(this.cropFilter),
      ...this.dimensionMeta().getAttributeFilterValuesBySlug(),
      ...(this.showArchivedFilter.on() ? { archived: null } : { archived: false }),
    };
  }

  getActions(cropVariety: CropVariety): Action[] {
    return this.dimensionRecordActions.getAll({
      id: cropVariety.id(),
      dimensionMetaNameSlug: this.dimensionMeta().nameSlug(),
      archived: cropVariety.archived(),
    });
  }

  private applyChange = () => refreshRows(this.newVarietiesTable, this.view, this.loader);

  private loadTableView(urlParams: unknown): Promise<void> {
    // The variety types first: the seeded choices are rebuilt from them while the selected values
    // of the restored filter are resolved.
    return namesApi.list('crop_variety_types', {}).then((types) => {
      this.varietyTypes = toChoices(types);
      return this.view.load(urlParams, {
        firstVisit: { filter: ARCHIVED_HIDDEN_FILTER },
      });
    });
  }

  private fetchPage = (query: CropVarietiesQuery): Promise<CropVarietiesPage> =>
    this.view.fetchPage(
      query,
      (options) =>
        cropVarietiesApi.listForGrid(
          // Archived is a filter condition; `archived` is omitted so the endpoint returns both.
          pageParams(query, 'name_prefix'),
          options
        ),
      this
    );

  toRow = (variety: CropVariety): CropVarietiesRow => {
    return {
      id: variety.id(),
      name: translate(variety.nameJson()),
      // Empty when users may not open a variety: the name cell then renders plain text.
      url: this.canViewDetails ? session.toTenantPath(variety.editUrl()) : '',
      anonymizedCode: variety.anonymizedCode() || '',
      cropName: variety.crop() ? translate(variety.crop().name_json) : '',
      varietyTypeName: variety.varietyType() ? translate(variety.varietyType().name_json) : '',
      // An organization has one plain name; its type is translated.
      originName: variety.origin() ? variety.origin().name : '',
      originTypeName: variety.originType ? translate(variety.originType.name_json) : '',
      origin2Name: variety.origin2() ? variety.origin2().name : '',
      origin2TypeName: variety.origin2Type ? translate(variety.origin2Type.name_json) : '',
      archived: !!variety.archived(),
      values: formatAttributes(variety.attributes()),
      actions: delegateActions(this, variety, this.view.reload),
    };
  };

  private onBulkDelete = (selection: IslandGridSelection) =>
    confirmBulkDelete(
      i18n.t(['crop_varieties_title', 'Crop Varieties'])().toLowerCase(),
      selection,
      (scope) => cropVarietiesApi.bulkRemove({ ...this.view.queryParams('name_prefix'), ...scope }),
      this.view.reload
    );
}

export let cropVarieties = {
  name: 'crop-varieties',
  viewModel: CropVarietiesScreen,
  template: template,
};

ko.components.register(cropVarieties.name, cropVarieties);
